import { Assets, Rectangle, Texture } from 'pixi.js';

/**
 * 도트 아틀라스 로더.
 * tools/slice_character.py 가 뱉은 <이름>.png + <이름>.json 을 읽어
 * 프레임 번호로 꺼내 쓸 수 있는 Texture 배열로 만든다.
 */

interface AtlasFrameJson {
  i: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface AtlasJson {
  image: string;
  scale: number;
  palette: string[];
  frames: AtlasFrameJson[];
}

export interface Atlas {
  /** 프레임 번호 순서대로 */
  frames: Texture[];
  /** 시트에서 뽑아낸 팔레트. 이펙트 색을 여기에 맞춘다. */
  palette: string[];
}

const cache = new Map<string, Atlas>();

/** 도트는 반드시 최근접 확대. 안 하면 흐려진다. */
export function makePixelPerfect(tex: Texture): Texture {
  tex.source.scaleMode = 'nearest';
  tex.source.autoGenerateMipmaps = false;
  return tex;
}

export async function loadAtlas(jsonPath: string): Promise<Atlas> {
  const hit = cache.get(jsonPath);
  if (hit) return hit;

  const res = await fetch(jsonPath);
  if (!res.ok) throw new Error(`아틀라스 JSON을 못 읽었다: ${jsonPath} (${res.status})`);
  const data = (await res.json()) as AtlasJson;

  // 이미지 경로는 JSON 파일과 같은 폴더 기준
  const dir = jsonPath.slice(0, jsonPath.lastIndexOf('/') + 1);
  const sheet = (await Assets.load(dir + data.image)) as Texture;
  makePixelPerfect(sheet);

  const frames: Texture[] = [];
  for (const f of data.frames) {
    frames[f.i] = new Texture({
      source: sheet.source,
      frame: new Rectangle(f.x, f.y, f.w, f.h),
    });
  }

  const atlas: Atlas = { frames, palette: data.palette };
  cache.set(jsonPath, atlas);
  return atlas;
}

export function getAtlas(jsonPath: string): Atlas | undefined {
  return cache.get(jsonPath);
}
